import { cryptoProxyHost, useCryptoProxy, getToken, SERVER_HOST } from '../SharedCommon/utils';
import cryptoUtils from './cryptoproxy/cryptoUtil';

export function getWebSocketUrl() {
  const token = getToken();
  if (!token) {
    return null;
  }

  if (useCryptoProxy) {
    const base = cryptoProxyHost.replace(/^http/, 'ws');
    return `${base.replace(/\/+$/, '')}/wss?token=${encodeURIComponent(token)}`;
  }

  let host = SERVER_HOST || '';
  if (host.startsWith('http://') || host.startsWith('https://')) {
    host = host.replace(/^https?:\/\//, '');
  }

  const isLocalHost = /^localhost(:\d+)?$/.test(host) || /^127\.0\.0\.1(:\d+)?$/.test(host);
  const protocol =
    isLocalHost && typeof window !== 'undefined' && window.location && window.location.protocol === 'http:'
      ? 'ws:'
      : 'wss:';

  return `${protocol}//${host.replace(/\/+$/, '')}/wss?token=${encodeURIComponent(token)}`;
}

export class ChatSocketManager {
  socket = null;
  owners = new Set();
  listeners = new Set();
  isExplicitClose = true;
  reconnectTimer = null;
  reconnectDelay = 2000;
  hasConnectedOnce = false;
  sessionUrl = null;
  sessionRejected = false;

  retain() {
    const owner = {};
    this.owners.add(owner);
    if (this.owners.size === 1) {
      window.addEventListener('storage', this.onStorage);
    }
    this.refreshSession();
    return () => {
      this.owners.delete(owner);
      if (this.owners.size === 0) {
        window.removeEventListener('storage', this.onStorage);
        this.closeSocket();
      }
    };
  }

  onStorage = (event) => {
    if (event.key === 'access_token' || event.key === null) {
      this.refreshSession();
    }
  };

  refreshSession() {
    const url = getWebSocketUrl();
    if (url !== this.sessionUrl) {
      this.closeSocket();
      this.sessionUrl = url;
      this.sessionRejected = false;
      this.hasConnectedOnce = false;
      this.reconnectDelay = 2000;
    }
    if (this.owners.size && url && !this.sessionRejected && !this.socket && !this.reconnectTimer) {
      this.isExplicitClose = false;
      this.connect();
    }
  }

  subscribe(groupId, callbacks) {
    const listener = { groupId, callbacks };
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getSoleSubscribedGroupId() {
    const groups = new Set(
      [...this.listeners].filter((listener) => listener.groupId != null).map((listener) => String(listener.groupId))
    );
    return groups.size === 1 ? groups.values().next().value : undefined;
  }

  emit(callback, data, groupId, scoped = false, groupListenersOnly = false) {
    for (const listener of this.listeners) {
      if (groupListenersOnly && listener.groupId == null) continue;
      if (scoped && listener.groupId != null && (groupId == null || String(groupId) !== String(listener.groupId))) {
        continue;
      }
      try {
        listener.callbacks[callback]?.(data);
      } catch (error) {
        console.error('WebSocket subscriber failed:', error);
      }
    }
  }

  connect() {
    if (this.socket || this.isExplicitClose || this.sessionRejected || !this.owners.size) return;
    const url = getWebSocketUrl();
    if (!url) {
      console.log('no init socket: missing token or URL');
      return;
    }

    this.sessionUrl = url;

    const WebSocketImpl =
      typeof WebSocket !== 'undefined' ? WebSocket : typeof window !== 'undefined' ? window.WebSocket : null;
    if (!WebSocketImpl) {
      console.error('WebSocket is not supported in this environment');
      return;
    }

    let ws;
    try {
      ws = new WebSocketImpl(url);
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      this.scheduleReconnect();
      return;
    }

    this.socket = ws;
    // Keep the existing sign-out helper connected to the shared transport.
    window.___socket = this;

    ws.onopen = () => {
      if (this.socket !== ws) {
        return;
      }
      console.log('socket connected!');
      if (this.hasConnectedOnce) {
        this.emit('reconnectedCallback');
      }
      this.hasConnectedOnce = true;
      this.reconnectDelay = 2000;
      this.emit('connectedCallback');
    };

    ws.onmessage = (event) => {
      if (this.socket !== ws) {
        return;
      }
      if (getWebSocketUrl() !== this.sessionUrl) {
        this.refreshSession();
        return;
      }
      try {
        let rawData = event.data;
        if (
          useCryptoProxy &&
          typeof rawData === 'string' &&
          !rawData.trim().startsWith('{') &&
          !rawData.trim().startsWith('[')
        ) {
          try {
            rawData = cryptoUtils.decryptSession(rawData);
          } catch (e) {
            console.warn('decryptSession failed:', e);
          }
        }
        const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
        if (!parsed) {
          return;
        }

        const { event: evt, data } = parsed;
        const groupId = parsed.groupId ?? data?.groupId;
        const callback = {
          newMessages: 'newMessageCallback',
          deleteMessage: 'deleteMessageCallback',
          groupTags: 'groupTagsCallback',
          newGroupInfo: 'newGroupInfoCallback',
          notifyMessage: 'notifyMessageCallback'
        }[evt];
        if (!callback) return;
        if (evt === 'newMessages') {
          // App listeners receive the original batch once, even without group IDs.
          this.emit(callback, data, undefined, true);
          if (!Array.isArray(data)) return;
          // Some servers put the group ID on each message instead of the envelope.
          if (groupId == null) {
            const groups = new Map();
            for (const message of data) {
              if (message?.groupId == null) continue;
              const key = String(message.groupId);
              if (!groups.has(key)) groups.set(key, []);
              groups.get(key).push(message);
            }
            // Legacy servers omit group IDs for the active chat. Only infer the
            // destination when all mounted chat subscribers agree on one group.
            if (groups.size === 0 && data.length) {
              const fallbackGroupId = this.getSoleSubscribedGroupId();
              if (fallbackGroupId != null) groups.set(fallbackGroupId, [...data]);
            }
            for (const [id, messages] of groups) {
              this.emit(
                callback,
                messages.sort((a, b) => b.time - a.time),
                id,
                true,
                true
              );
            }
            return;
          }
          this.emit(
            callback,
            [...data].sort((a, b) => b.time - a.time),
            groupId,
            true,
            true
          );
        } else {
          const routedGroupId = groupId ?? (evt !== 'notifyMessage' ? this.getSoleSubscribedGroupId() : undefined);
          this.emit(callback, data, routedGroupId, true);
        }
      } catch (err) {
        console.error('Error handling WebSocket message:', err);
      }
    };

    ws.onerror = (error) => {
      if (this.socket !== ws) {
        return;
      }
      console.log('WebSocket error:', error);
    };

    ws.onclose = (event) => {
      if (this.socket !== ws) {
        return;
      }
      console.log(`WebSocket closed: code=${event.code}, reason=${event.reason}`);
      this.socket = null;
      if (event.code === 4001) {
        this.sessionRejected = true;
        this.closeSocket();
      }
      this.emit('disconnectedCallback');
      if (!this.isExplicitClose && event.code !== 4001 && getToken()) {
        this.scheduleReconnect();
      }
    };
  }

  scheduleReconnect() {
    if (this.isExplicitClose || this.reconnectTimer) {
      return;
    }

    const delay = this.reconnectDelay + Math.floor(Math.random() * 1000);
    console.log(`Scheduling WebSocket reconnect in ${delay}ms`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isExplicitClose && getToken()) {
        this.refreshSession();
      }
    }, delay);

    this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000);
  }

  closeSocket() {
    this.isExplicitClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      try {
        s.close();
      } catch (e) {
        // ignore
      }
    }
  }

  close() {
    this.closeSocket();
  }
}

export const chatSocket = new ChatSocketManager();
